import { useState, useEffect } from 'react';

interface CalendarDay {
  date: Date;
  currentMonth: boolean;
}

interface CalendarMonth {
  year: number;
  month: number;
  monthName: string;
  days: CalendarDay[];
}

const DAYS_OF_WEEK_PL = ['pn', 'wt', 'śr', 'cz', 'pt', 'so', 'nd'];
const MONTHS_PL = [
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
];

const API_URL = process.env.REACT_APP_API_HOST ?? '';

const Available = () => {
  const [bookedDates, setBookedDates] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMonth, setViewMonth] = useState(() => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), 1);
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  useEffect(() => {
    const fetchBookedDates = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/api/calendar/booked-dates`);
        if (!response.ok) throw new Error('Failed to fetch');
        const dates: string[] = await response.json();
        setBookedDates(new Set(dates));
      } catch {
        setError('Nie udało się załadować kalendarza. Spróbuj ponownie później.');
      } finally {
        setLoading(false);
      }
    };
    fetchBookedDates();
  }, []);

  const isBooked = (date: Date): boolean => {
    const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return bookedDates.has(iso);
  };

  const isToday = (date: Date): boolean =>
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate();

  const isPast = (date: Date): boolean => date < today && !isToday(date);

  const generateMonth = (monthStart: Date): CalendarMonth => {
    const year = monthStart.getFullYear();
    const month = monthStart.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    let startWeekday = (firstDay.getDay() + 6) % 7;

    const cells: CalendarDay[] = [];
    for (let i = 0; i < startWeekday; i++) {
      const d = new Date(year, month, -startWeekday + i + 1);
      cells.push({ date: d, currentMonth: false });
    }
    for (let d = 1; d <= lastDay.getDate(); d++) {
      cells.push({ date: new Date(year, month, d), currentMonth: true });
    }
    while (cells.length % 7 !== 0) {
      cells.push({ date: new Date(year, month + 1, cells.length - lastDay.getDate() - startWeekday + 1), currentMonth: false });
    }

    return { year, month, monthName: MONTHS_PL[month], days: cells };
  };

  const monthsToShow = [0, 1, 2, 3].map(offset =>
    generateMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + offset, 1))
  );

  const shift = (n: number) =>
    setViewMonth(m => new Date(m.getFullYear(), m.getMonth() + n, 1));

  const monthName = (d: Date) => d.toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' });

  return (
    <div>
      {/* HERO */}
      <div className="shell">
        <section className="hero" style={{ gridTemplateColumns: '1fr', paddingBottom: 40 }}>
          <div style={{ maxWidth: 900 }}>
            <span className="eyebrow">Dostępność · {viewMonth.getFullYear()}</span>
            <h1 className="display hero-headline" style={{ fontSize: 'clamp(48px, 6vw, 96px)' }}>
              Kiedy <em>możemy</em><br />Was przyjąć.
            </h1>
            <p className="hero-lede" style={{ maxWidth: 720 }}>
              Kalendarz aktualizujemy po każdej rezerwacji. Jeśli widzisz wolny termin —
              napisz, potwierdzimy dostępność w ciągu doby.
            </p>
          </div>
        </section>
      </div>

      {/* LEGEND + NAV */}
      <div className="shell">
        <section style={{ padding: '20px 0 32px' }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            borderTop: '1px solid var(--ink)', borderBottom: '1px solid var(--ink)',
            padding: '14px 0', flexWrap: 'wrap', gap: 16
          }}>
            <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 16, height: 16, background: 'var(--cream-2)', border: '1px solid var(--line)' }} />
                <span className="small-caps">wolny</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 16, height: 16, background: 'var(--terracotta)' }} />
                <span className="small-caps">zarezerwowany</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 16, height: 16, background: 'var(--cream)', border: '2px solid var(--ink)' }} />
                <span className="small-caps">dziś</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost" style={{ padding: '8px 16px', fontSize: 11 }} onClick={() => shift(-4)}>
                ← wcześniej
              </button>
              <button className="btn btn-ghost" style={{ padding: '8px 16px', fontSize: 11 }} onClick={() => {
                const t = new Date();
                setViewMonth(new Date(t.getFullYear(), t.getMonth(), 1));
              }}>
                dziś
              </button>
              <button className="btn btn-ghost" style={{ padding: '8px 16px', fontSize: 11 }} onClick={() => shift(4)}>
                później →
              </button>
            </div>
          </div>
        </section>
      </div>

      {/* CALENDAR GRID */}
      <div className="shell">
        <section className="section" style={{ paddingTop: 0 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <div className="display-italic" style={{ fontSize: 28, color: 'var(--ink-soft)' }}>
                Ładowanie kalendarza…
              </div>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <div style={{ color: 'var(--terracotta)', fontSize: 16 }}>{error}</div>
            </div>
          ) : (
            <div
              style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 48 }}
              className="months-cal"
            >
              {monthsToShow.map((month, idx) => (
                <div key={idx}>
                  <div className="display" style={{ fontSize: 28, marginBottom: 14, textTransform: 'capitalize' }}>
                    {monthName(new Date(month.year, month.month, 1))}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
                    {DAYS_OF_WEEK_PL.map(d => (
                      <div key={d} className="mono olive" style={{
                        textAlign: 'center', padding: '6px 0', textTransform: 'uppercase'
                      }}>{d}</div>
                    ))}
                    {month.days.map((day, i) => {
                      if (!day.currentMonth) return <div key={i} />;
                      const booked = isBooked(day.date);
                      const past = isPast(day.date);
                      const tod = isToday(day.date);
                      return (
                        <div key={i} style={{
                          aspectRatio: '1',
                          background: booked ? 'var(--terracotta)' : past ? 'transparent' : 'var(--cream-2)',
                          color: booked ? 'var(--cream)' : past ? 'var(--stone-light)' : 'var(--ink)',
                          display: 'flex', flexDirection: 'column',
                          alignItems: 'flex-start', justifyContent: 'flex-start',
                          padding: 8, fontSize: 13,
                          border: tod ? '2px solid var(--ink)' : '1px solid var(--line-soft)',
                          position: 'relative',
                        }}>
                          <div style={{ fontFamily: 'var(--ff-display)', fontSize: 18 }}>
                            {day.date.getDate()}
                          </div>
                          {!booked && !past && (
                            <div className="mono" style={{
                              position: 'absolute', bottom: 4, right: 4, fontSize: 9,
                              color: 'var(--olive)', letterSpacing: '0.06em'
                            }}>wolny</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* CTA */}
      <div style={{ background: 'var(--olive)', color: 'var(--cream)' }}>
        <div className="shell">
          <section className="section" style={{ textAlign: 'center' }}>
            <h2 className="display" style={{
              fontSize: 'clamp(32px, 4.5vw, 56px)', margin: 0, color: 'var(--cream)'
            }}>
              Widzisz swój <em>termin</em>?
            </h2>
            <p style={{
              maxWidth: 560, margin: '18px auto 26px',
              color: 'rgba(245,239,228,0.85)', fontSize: 16
            }}>
              Napisz, a potwierdzimy dostępność i podeślemy szczegóły pobytu.
            </p>
            <a
              className="btn btn-ghost"
              style={{ color: 'var(--cream)', borderColor: 'var(--cream)' }}
              href="/contact"
            >
              Napisz do nas →
            </a>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Available;
